import { Component, signal, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

import { ProgressComponent, MessageService } from '@factor_ec/ui';
import { GoogleTagManagerService } from '@factor_ec/utils';

import { AppService } from '@/core/app.service';
import { ApiService } from '@/core/api.service';
import { ErrorMessageService } from '@/core/error-message.service';
import { Router } from '@angular/router';
import { RequestData } from '@/core/models/request-data';

/**
 * @description Componente para gestionar la recuperación de contraseña
 * @class ForgotPasswordComponent
 */
@Component({
  selector: 'app-forgot-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatInputModule,
    ProgressComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordComponent {
  // Dependency injection
  appService = inject(AppService);
  private apiService = inject(ApiService);
  private dialogRef =
    inject<MatDialogRef<ForgotPasswordComponent>>(MatDialogRef);
  private formBuilder = inject(FormBuilder);
  private googleTagManagerService = inject(GoogleTagManagerService);
  private messageService = inject(MessageService);
  router = inject(Router);

  // Properties
  form: FormGroup = new FormGroup({});
  submitting = signal<boolean>(false);

  /**
   * @description Constructor del componente
   * @constructor
   */
  constructor() {
    this.initForm();
  }

  /**
   * @description Inicializa el formulario reactivo
   * @private
   */
  private initForm(): void {
    this.form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  /**
   * @description Envía el formulario para solicitar recuperación de contraseña
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.form.valid) {
      try {
        const data: RequestData = {
          microServiceName: 'WSMPUsersManagement',
          resourcePath: `/user/recovery-password-request`,
          method: 'POST',
          useToken: true,
          body: this.form.value,
          bodyEncrypted: true
        };
        this.submitting.set(true);
        this.form.disable();
        await this.apiService.call(data);
        this.submitting.set(false);
        this.dialogRef.close();
        this.router.navigate(
          ['/reset-password-success', { email: data.body.email }],
          {
            skipLocationChange: true
          }
        );
        this.googleTagManagerService.addVariable({ event: 'forgot_password' });
      } catch (err: any) {
        this.submitting.set(false);
        this.form.enable();
      }
    }
  }
}
